/**
 * STTY — Typo-tolerant search autocomplete
 * Uses Fuse.js for fuzzy matching against the product catalogue.
 * Products are fetched once on first focus and cached in memory.
 *
 * Implements the ARIA 1.2 combobox pattern: focus stays on the input and
 * aria-activedescendant points at the highlighted option, so the options
 * are never separate tab stops.
 */
(function () {

  var FUSE_THRESHOLD   = 0.4;   // 0 = exact, 1 = match anything
  var MIN_CHARS        = 2;     // start searching after this many characters
  var MAX_RESULTS      = 6;
  var PRODUCTS_PER_PAGE = 250;

  var productIndex = null;   // Fuse instance
  var fuseReady    = false;
  var fetchPending = false;
  var idCounter    = 0;      // gives each dropdown a unique id to reference

  /* ── Build search index ─────────────────────────────────── */

  function buildIndex(products) {
    productIndex = new Fuse(products, {
      threshold: FUSE_THRESHOLD,
      minMatchCharLength: MIN_CHARS,
      keys: [
        { name: 'title',        weight: 0.6 },
        { name: 'tags',         weight: 0.25 },
        { name: 'product_type', weight: 0.1 },
        { name: 'vendor',       weight: 0.05 }
      ]
    });
    fuseReady = true;
  }

  function fetchProducts(page, accumulated) {
    accumulated = accumulated || [];
    page = page || 1;

    return fetch('/collections/all/products.json?limit=' + PRODUCTS_PER_PAGE + '&page=' + page)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var batch = data.products || [];
        var all   = accumulated.concat(batch);
        if (batch.length === PRODUCTS_PER_PAGE) {
          return fetchProducts(page + 1, all);
        }
        return all;
      });
  }

  function ensureIndex(cb) {
    if (fuseReady) { cb(); return; }
    if (fetchPending) { return; }
    fetchPending = true;
    fetchProducts().then(function (products) {
      buildIndex(products);
      fetchPending = false;
      cb();
    }).catch(function () {
      fetchPending = false;
    });
  }

  /* ── Autocomplete UI ────────────────────────────────────── */

  function createDropdown(inputEl) {
    idCounter += 1;

    var list = document.createElement('ul');
    list.id = 'search-autocomplete-' + idCounter;
    list.className = 'search-autocomplete';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Search suggestions');
    list.hidden = true;
    list.activeIndex = -1;

    inputEl.setAttribute('autocomplete', 'off');
    inputEl.setAttribute('role', 'combobox');
    inputEl.setAttribute('aria-autocomplete', 'list');
    inputEl.setAttribute('aria-expanded', 'false');
    inputEl.setAttribute('aria-controls', list.id);

    inputEl.parentNode.style.position = 'relative';
    inputEl.parentNode.appendChild(list);

    return list;
  }

  function openList(list, inputEl) {
    list.hidden = false;
    inputEl.setAttribute('aria-expanded', 'true');
    setActive(list, inputEl, -1);
  }

  function closeList(list, inputEl) {
    list.hidden = true;
    inputEl.setAttribute('aria-expanded', 'false');
    inputEl.removeAttribute('aria-activedescendant');
    list.activeIndex = -1;
  }

  function setActive(list, inputEl, index) {
    var options = list.querySelectorAll('[role="option"]');
    if (!options.length) return;

    Array.prototype.forEach.call(options, function (opt) {
      opt.setAttribute('aria-selected', 'false');
    });

    if (index < 0 || index >= options.length) {
      inputEl.removeAttribute('aria-activedescendant');
      list.activeIndex = -1;
      return;
    }

    var active = options[index];
    active.setAttribute('aria-selected', 'true');
    inputEl.setAttribute('aria-activedescendant', active.id);
    list.activeIndex = index;

    if (active.scrollIntoView) active.scrollIntoView({ block: 'nearest' });
  }

  function buildOption(list, index, url, innerHtml, extraClass) {
    var li = document.createElement('li');
    li.className = 'search-autocomplete__item' + (extraClass ? ' ' + extraClass : '');
    li.id = list.id + '-option-' + index;
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    li.dataset.url = url;
    li.innerHTML = innerHtml;
    return li;
  }

  function renderResults(list, inputEl, results, query) {
    list.innerHTML = '';

    if (!results.length) {
      closeList(list, inputEl);
      return;
    }

    results.forEach(function (r, i) {
      var product = r.item;

      var img = '';
      if (product.images && product.images.length) {
        img = '<img src="' + product.images[0].src.replace(/(\.[^.]+)$/, '_80x80$1') +
              '" alt="" width="40" height="40" aria-hidden="true">';
      }

      var price = product.variants && product.variants[0]
        ? '£' + (product.variants[0].price / 100).toFixed(2)
        : '';

      var inner =
        '<span class="search-autocomplete__link">' +
          '<span class="search-autocomplete__thumb">' + img + '</span>' +
          '<span class="search-autocomplete__info">' +
            '<span class="search-autocomplete__name">' + escapeHtml(product.title) + '</span>' +
            (price ? '<span class="search-autocomplete__price">' + price + '</span>' : '') +
          '</span>' +
        '</span>';

      list.appendChild(buildOption(list, i, '/products/' + product.handle, inner));
    });

    // "See all results" is the final option so arrow keys reach it too
    var allInner = '<span class="search-autocomplete__all">See all results for &ldquo;' +
                   escapeHtml(query) + '&rdquo; &rarr;</span>';
    list.appendChild(buildOption(
      list,
      results.length,
      '/search?q=' + encodeURIComponent(query) + '&type=product',
      allInner,
      'search-autocomplete__footer'
    ));

    openList(list, inputEl);
  }

  function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ── Wire up an input ───────────────────────────────────── */

  function initInput(inputEl) {
    if (!inputEl) return;

    var list     = createDropdown(inputEl);
    var debounce = null;

    inputEl.addEventListener('focus', function () {
      ensureIndex(function () {});
    });

    inputEl.addEventListener('input', function () {
      clearTimeout(debounce);
      var q = inputEl.value.trim();

      if (q.length < MIN_CHARS) {
        closeList(list, inputEl);
        return;
      }

      debounce = setTimeout(function () {
        if (!fuseReady) return;
        var results = productIndex.search(q, { limit: MAX_RESULTS });
        renderResults(list, inputEl, results, q);
      }, 150);
    });

    inputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        // Keep focus on the input — moving it would strand the user
        closeList(list, inputEl);
        return;
      }

      if (list.hidden) return;

      var options = list.querySelectorAll('[role="option"]');
      var count   = options.length;
      var index   = list.activeIndex;

      if (!count) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(list, inputEl, index + 1 >= count ? 0 : index + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(list, inputEl, index - 1 < 0 ? count - 1 : index - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActive(list, inputEl, 0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActive(list, inputEl, count - 1);
      } else if (e.key === 'Enter') {
        // With nothing highlighted, fall through and let the form submit
        if (index >= 0 && options[index]) {
          e.preventDefault();
          window.location.href = options[index].dataset.url;
        }
      } else if (e.key === 'Tab') {
        closeList(list, inputEl);
      }
    });

    // Keep the dropdown open while it is being clicked
    list.addEventListener('mousedown', function (e) {
      e.preventDefault();
    });

    list.addEventListener('click', function (e) {
      var option = e.target.closest('[role="option"]');
      if (option && option.dataset.url) {
        window.location.href = option.dataset.url;
      }
    });

    inputEl.addEventListener('blur', function () {
      setTimeout(function () { closeList(list, inputEl); }, 150);
    });
  }

  /* ── Init ───────────────────────────────────────────────── */

  document.addEventListener('DOMContentLoaded', function () {
    initInput(document.querySelector('.nav-search input[type="search"]'));
    initInput(document.querySelector('.mobile-search input[type="search"]'));
  });

})();
